import { OrderItem } from "../../types";
import Decimal from "decimal.js";
import { getDiscountPrice } from "../shared/getDiscountPrice";
import { getM2Value } from "./getM2Value";
import { isMissingLinkedDoorItem } from "./isMissingLinkedDoorItem";
import { isCarcaseItem } from "./isCarcaseItem";

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * Gets the unit sale price of an order item, after discount.
 *
 * For carcase items sold by the m2, the price is scaled by the item's m2
 * value (derived from its edited width/height). Items that carry a
 * rawOverheadContribution snapshot keep the overhead portion fixed and
 * scale only the raw material portion by area.
 *
 * @param {OrderItem} item
 * @returns string - unit price with 2 decimal places
 * @throws If the item is a MISSING_LINKED_DOOR item (cannot be priced)
 */
export function getOrderItemPrice(item: OrderItem): string {
  if (isMissingLinkedDoorItem(item))
    throw new Error("Missing linked door item cannot be priced");

  if (isCarcaseItem(item) && item.soldByM2) {
    const m2 = getM2Value(item);

    // New-model M2 items snapshot their Raw Overhead contribution when added
    // to the order. Keep the overhead portion fixed, scale only the raw
    // material portion by area, then discount the total. 
    if (isFiniteNumber(item.rawOverheadContribution)) {
      const priceBeforeDiscount = new Decimal(item.price)
        .minus(item.rawOverheadContribution)
        .mul(m2)
        .add(item.rawOverheadContribution);

      return getDiscountPrice(priceBeforeDiscount.toString(), item.discount);
    }

    // Legacy M2 pricing: discount the per-m2 price, then scale by area.
    const price = getDiscountPrice(item.price, item.discount);
    return new Decimal(price).mul(m2).toFixed(2);
  }

  return getDiscountPrice(item.price, item.discount);
}
